import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Send, X, Bot, User as UserIcon } from "lucide-react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const renderBold = (txt) => txt.split(/\*\*(.+?)\*\*/g).map((s, i) => (i % 2 ? <b key={i}>{s}</b> : s));

export default function AiAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    const next = [...messages, { role: "user", content: text }, { role: "assistant", content: "" }];
    setMessages(next);
    setStreaming(true);

    try {
      const r = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      if (!r.ok || !r.body) throw new Error("stream_error");
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const data = JSON.parse(line.slice(5).trim());
            if (data.delta) {
              setMessages((prev) => {
                const arr = [...prev];
                arr[arr.length - 1] = { role: "assistant", content: arr[arr.length - 1].content + data.delta };
                return arr;
              });
            }
            if (data.session_id) setSessionId(data.session_id);
          } catch (e) {}
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const arr = [...prev];
        arr[arr.length - 1] = { role: "assistant", content: "Desculpe, falha ao responder. Tente novamente." };
        return arr;
      });
    } finally {
      setStreaming(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-fab"
          className="fixed bottom-24 right-5 md:bottom-6 md:right-6 z-40 bg-gradient-to-br from-[#7c3aed] to-[#4c1d95] text-white rounded-full w-14 h-14 shadow-lg hover:scale-105 transition-transform flex items-center justify-center"
          aria-label="Assistente IA"
        >
          <Sparkles className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div className="fixed z-50 inset-x-4 bottom-24 md:bottom-6 md:right-6 md:inset-x-auto md:w-[400px] h-[calc(100dvh-8rem)] max-h-[640px] md:h-[560px] bg-white rounded-2xl border border-slate-200 shadow-2xl flex flex-col overflow-hidden" data-testid="ai-panel">
          <div className="shrink-0 flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#1e3a8a] to-[#4c1d95] text-white">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <p className="font-semibold text-sm leading-tight">Assistente AMC</p>
                <p className="text-[10px] text-white/70 leading-tight">GPT-5.2 • Online</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded-lg" data-testid="ai-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3 bg-slate-50/50">
            {messages.length === 0 && (
              <div className="text-center py-8 px-4">
                <div className="w-12 h-12 rounded-full bg-[#7c3aed]/10 flex items-center justify-center mx-auto mb-3">
                  <Bot className="w-6 h-6 text-[#7c3aed]" />
                </div>
                <p className="text-sm font-semibold text-slate-900">Olá! Como posso ajudar?</p>
                <p className="text-xs text-slate-500 mt-1">Pergunte sobre orçamentos, escolas e indicadores do sistema.</p>
                <div className="mt-4 space-y-2 text-left">
                  {[
                    "Quantos orçamentos estão aguardando?",
                    "Qual escola tem mais orçamentos?",
                    "Resuma o status geral dos orçamentos",
                  ].map((s, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(s)}
                      className="w-full text-xs text-left bg-white border border-slate-200 rounded-lg px-3 py-2 hover:border-[#7c3aed] text-slate-700"
                      data-testid={`ai-suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && (
                  <div className="w-7 h-7 rounded-full bg-[#7c3aed] flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className={`max-w-[80%] min-w-0 break-words rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#1e3a8a] text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                  {m.content ? renderBold(m.content) : (streaming && i === messages.length - 1 ? "..." : "")}
                </div>
                {m.role === "user" && (
                  <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
                    <UserIcon className="w-4 h-4 text-slate-600" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="shrink-0 p-3 border-t border-slate-200 bg-white">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Pergunte algo..."
                disabled={streaming}
                data-testid="ai-input"
              />
              <Button
                onClick={send}
                disabled={streaming || !input.trim()}
                className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
                data-testid="ai-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
